import { useState } from 'react'
import { KeyRound, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDuration } from '@/lib/format'
import { useCredentialDetails } from '@/hooks/useCredentials'
import type { CredentialDetails } from '@/lib/api/credentials'
import type { Credential } from '@/lib/api/types'
import { CredentialSecretModal } from '@/components/credentials/CredentialSecretModal'
import { RowActions, type RecordAction, type RecordPermissions } from './RowActions'

interface CredentialRecordTableProps {
  records: Credential[]
  onAction: (action: RecordAction, record: Credential) => void
  /** Which per-row actions the signed-in role may take. */
  permissions: RecordPermissions
  /** True while a search is in flight, so the empty state does not flash. */
  loading?: boolean
  /** Set when the search itself failed. */
  error?: string | null
}

const columns = ['Name & Tags', 'Notes', 'Elevation', 'Last Rotated By', 'Secret', 'Actions']

/** Admin record list with per-row rotate/purge actions. */
export function CredentialRecordTable({
  records,
  onAction,
  permissions,
  loading,
  error,
}: CredentialRecordTableProps) {
  if (error) return <Placeholder tone="error">{error}</Placeholder>
  if (loading && records.length === 0) return <Placeholder>Searching credentials…</Placeholder>
  if (records.length === 0) return <Placeholder>No credentials match this search</Placeholder>

  return (
    <div className="border-line bg-surface overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[980px] border-collapse text-sm">
        <thead>
          <tr className="border-line border-b">
            {columns.map((col) => (
              <th
                key={col}
                className={cn(
                  'text-fg-subtle px-4 py-3 font-mono text-[11px] font-semibold tracking-[0.08em] uppercase',
                  col === 'Actions' ? 'text-right' : 'text-left',
                )}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr
              key={record.id}
              className="border-line hover:bg-surface-2 border-b transition-colors last:border-0"
            >
              <td className="px-4 py-3.5">
                <p className="text-fg font-mono font-medium">{record.name}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {record.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="bg-surface-3 text-fg-muted rounded px-1.5 py-0.5 font-mono text-[10px]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3.5">
                <NotesCell notes={record.notes} />
              </td>
              <td className="text-fg-muted px-4 py-3.5 font-mono">
                {formatDuration(record.elevation_duration_seconds)}
              </td>
              <td className="text-fg-muted px-4 py-3.5 font-mono">
                {record.last_rotated_by?.trim() || 'Not available'}
              </td>
              <td className="px-4 py-3.5">
                <SecretCell record={record} />
              </td>
              <td className="px-4 py-3.5">
                <RowActions record={record} permissions={permissions} onAction={onAction} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Clamp long notes so one record cannot widen or dominate the table. */
function NotesCell({ notes }: { notes?: string }) {
  const note = notes?.trim()
  if (!note) return <span className="text-fg-subtle">Not provided</span>

  return (
    <p
      className="text-fg-muted line-clamp-2 w-64 max-w-64 break-all whitespace-normal"
      title={note}
    >
      {note}
    </p>
  )
}

/**
 * Masked secret that opens the shared credential details dialog.
 *
 * Opening calls get-credential-details only. The shared dialog reserves
 * get-credential-secret for each explicit Copy Password click so backend audit
 * logging corresponds to real copy attempts.
 */
function SecretCell({ record }: { record: Credential }) {
  const [open, setOpen] = useState(false)
  const [details, setDetails] = useState<CredentialDetails | null>(null)
  const detailsRequest = useCredentialDetails({ onSuccess: setDetails })

  const openModal = () => {
    if (detailsRequest.isPending) return
    setDetails(null)
    setOpen(true)
    // A fetch failure toasts via the hook; drop the dialog rather than leaving it
    // spinning.
    detailsRequest.mutate(record.id, { onError: () => setOpen(false) })
  }

  const close = () => {
    setOpen(false)
    setDetails(null)
    detailsRequest.reset()
  }

  return (
    <div className="flex items-center gap-2">
      <span
        title="Stored encrypted. Open to copy it."
        className="text-fg-subtle font-mono text-xs tracking-widest"
      >
        ••••••••••••••••
      </span>
      <button
        onClick={openModal}
        disabled={detailsRequest.isPending}
        aria-label={`View credential details for ${record.name}`}
        title="View credential details"
        className={cn(
          'hover:bg-surface-3 text-fg-subtle hover:text-fg grid size-7 shrink-0 place-items-center rounded-md transition-colors disabled:cursor-not-allowed',
        )}
      >
        {detailsRequest.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <KeyRound className="size-4" />
        )}
      </button>

      {/* Admin access is not time-boxed, so no `expiresAt` — copy stays available. */}
      <CredentialSecretModal
        open={open}
        loading={detailsRequest.isPending}
        details={details}
        credentialName={record.name}
        onClose={close}
      />
    </div>
  )
}

function Placeholder({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div className="border-line bg-surface grid place-items-center rounded-lg border py-16 text-center">
      <p
        className={cn(
          'font-mono text-sm',
          tone === 'error' ? 'text-critical-bright' : 'text-fg-muted',
        )}
      >
        {children}
      </p>
    </div>
  )
}
